import _, { includes } from "lodash";

import { SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST } from "@/utils/constants";
import { ProposalWithOwner } from "@/utils/types";
import { getFormattedSuggestion } from "lib/frontend/cm/utils/logic/helpers/getFormattedSuggestion";
import { LogicProposalComponent } from "lib/frontend/cm/utils/types";
import { getInCycleSalaryIncomeFrequency } from "lib/shared/compensation/pay-frequency";
import { Proposal, SalaryIncomeFrequency, Review, User, Range, Company } from "prisma/cm/client";

import { roundOffCentsInteger } from "./cash";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { getCurrencyOffInstance, treatLocalCurrencyAs } from "../currency/getCurrencyField";

export function roundProposalValue({
  value,
  proposal,
  company,
}: {
  value: number | null;
  proposal: Proposal;
  company?: Company;
}) {
  if (_.isNull(value)) return null;
  // @ts-expect-error TS2345
  const decimals = decimalsForProposal({ proposal: proposal, company });
  return decimals ? Math.round(value) : roundOffCentsInteger(value);
}

export const decimalsForProposal = ({
  proposal,
  company,
}: {
  proposal: ProposalWithOwner;
  company?: Company;
}) => (proposalNeedsDecimals({ proposal, company }) ? 2 : 0);

export const decimalsForUserNominal = ({ user, company }: { user: User; company?: Company }) => {
  if (company?.alwaysShowDecimals) return 2;

  return includes(SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST, user.salaryIncomeFrequency) ? 2 : 0;
};

export function decimalsForRangeNominal({ range, company }: { range: Range; company?: Company }) {
  if (company?.alwaysShowDecimals) return 2;

  return includes(SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST, range.salaryIncomeFrequency) ? 2 : 0;
}

export function proposalNeedsDecimals({
  proposal,
  company,
}: {
  proposal?: ProposalWithOwner;
  company?: Company;
}) {
  //@ts-expect-error TS2551
  if (company?.alwaysShowDecimals || proposal?.owner?.company?.alwaysShowDecimals) return true;

  // COM-1006 - % diff issue.
  if (proposal?.raise || proposal?.oteIncrease) {
    const base = proposal?.oteIncrease || proposal?.raise || 0;
    if (base % 1 > 0) return true;
  }

  const salaryIncomeFrequency = proposal
    ? getInCycleSalaryIncomeFrequency({ proposalWithOwner: proposal })
    : null;

  return includes(
    SALARY_INCOME_FREQUENCY_DECIMAL_WHITELIST,
    salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
  );
}

export function formatSuggestedValue({
  proposal,
  review,
  targetCurrency,
  logicComponent,
  company,
}: {
  proposal: ProposalWithOwner;
  review: Review;
  targetCurrency: string;
  logicComponent: LogicProposalComponent;
  company: Company;
}) {
  // Determine the key for the suggested value and note based on the logic component
  const suggestedKey = logicComponent === "bonus" ? "suggestedBonus" : "suggestedRaise";
  const noteKey = logicComponent === "bonus" ? "suggestedBonusNote" : "suggestedRaiseNote";

  const engineResults = _.get(
    proposal,
    ["logicSuggestedValues", logicComponent, "engineResults"],
    null,
  );
  const suggested = proposal[suggestedKey] ?? _.get(engineResults, "reviewSuggestion", null);
  const notes = _.get(engineResults, "suggestedNotes", []);

  let result: {
    raw: number | null;
    formatted: string | null;
    tooltip: string;
  } | null = null;

  if (!engineResults && _.isNumber(suggested)) {
    const suggestedConverted = getConvertedCurrency({
      value: suggested,
      options: {
        sourceCurrency: proposal.owner.salaryCurrencyCode,
        targetCurrency: treatLocalCurrencyAs(
          targetCurrency,
          getCurrencyOffInstance(proposal.owner),
        ),
        exchangeRates: review.exchangeRates,
        // Assuming decimalsForProposal can handle both cases correctly

        decimals: decimalsForProposal({ proposal, company }),
      },
    });

    result = {
      raw: _.isNumber(suggested) ? suggested / 100 : null,
      formatted: suggestedConverted.formatted,
      // @ts-expect-error TS2322
      tooltip: proposal[noteKey] ? proposal[noteKey] : "",
    };
  } else {
    const rootNode = _.get(proposal, ["logicSuggestedValues", logicComponent, "rootNode"], null);
    const formatted =
      getFormattedSuggestion({
        company,
        rootNode,
        // @ts-expect-error TS2322
        proposal: proposal,
        review,
        engineResults,
        defaultCurrency: targetCurrency,
        component: logicComponent,
      }) ?? null;

    result = {
      raw: _.isNumber(suggested) ? suggested / 100 : null,
      formatted: formatted,
      tooltip: proposal[noteKey] ? proposal[noteKey] : notes.length ? notes.join("<br />") : "",
    };
  }

  return result;
}
